/**
 * Customer-facing money presentation.
 *
 * Proposal interactive/PDF item prices use SGD currency formatting.
 * VO commercial document / Price Adjustment intentionally use `$` with
 * en-SG grouping (historical VO chrome) — keep both authorities separate.
 */

/** Proposal / D-Tools item price: `S$1,234.56` via en-SG SGD currency. */
export function renderProposalMoney(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return "—";
  try {
    return new Intl.NumberFormat("en-SG", { style: "currency", currency: "SGD" }).format(v);
  } catch {
    return `S$${v.toFixed(2)}`;
  }
}

/** VO absolute amount: `$1,234.56` (no leading +). */
export function renderVoAbsoluteMoney(n: number): string {
  return `$${Math.abs(n).toLocaleString("en-SG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * VO signed amount used on line cards: `+$1,234.56` / `-$1,234.56` / `$0.00`.
 * Preserves existing VariationOrderDocument presentation exactly.
 */
export function renderVoMoneyDelta(n: number): string {
  const sign = n < 0 ? "-" : n > 0 ? "+" : "";
  return `${sign}$${Math.abs(n).toLocaleString("en-SG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * VO Price Adjustment signed amount: `-$1,234.56` for negatives, `$1,234.56` otherwise
 * (no leading +). Preserves VoPriceAdjustmentSection presentation exactly.
 */
export function renderVoSignedMoney(n: number): string {
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString("en-SG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
